import { useState } from 'react';
import { Plus, Moon, Sun, Copy, Clipboard, Trash2, GripVertical } from 'lucide-react';
import { IngredientAutocomplete } from './IngredientAutocomplete';
import { AISuggestButton } from './AISuggestButton';
import { type PlanEditorMeal, type PlanEditorItem, type MealType } from '../lib/api';

interface Props {
  meal: PlanEditorMeal;
  patientId: string | null;
  /** Contesto AI: target kcal del giorno + altri parametri formattati. */
  aiContext?: string;
  onChange: (next: PlanEditorMeal) => void;
  onRemove: () => void;
  /** Variante ON/OFF (slot non-fasting con room per ulteriori varianti). */
  variantLabel?: string | null;
  /** Permette aggiungere nuova variante A→D nel slot. */
  canAddVariant: boolean;
  nextVariantLabel: string | null;
  onAddVariant: () => void;
  /** Copia il pasto negli appunti del wizard. */
  onCopy: () => void;
  /** Incolla dagli appunti se presente. */
  onPaste: () => void;
  hasClipboard: boolean;
}

/**
 * Sprint 9.B — Singolo card pasto editor.
 * Gestisce: name, lista ingredienti con autocomplete+drag&drop, toggle fasting,
 * AI suggest, varianti (A/B/C/D), copy/paste pasto.
 */
export function MealEditorCard({
  meal,
  patientId,
  aiContext,
  onChange,
  onRemove,
  canAddVariant,
  nextVariantLabel,
  onAddVariant,
  onCopy,
  onPaste,
  hasClipboard,
}: Props) {
  const isFasting = meal.is_fasting === 1;
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const updateMeal = (patch: Partial<PlanEditorMeal>) => onChange({ ...meal, ...patch });

  const addItem = () => {
    const newItem: PlanEditorItem = { ingredient: '', quantity: '', unit: 'g', calories: null };
    updateMeal({ items: [...meal.items, newItem] });
  };

  const updateItem = (idx: number, patch: Partial<PlanEditorItem>) => {
    const next = meal.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    updateMeal({ items: next });
  };

  const removeItem = (idx: number) => {
    updateMeal({ items: meal.items.filter((_, i) => i !== idx) });
  };

  const toggleFasting = () => {
    if (!isFasting) {
      // Diventa fasting: svuota items e variant
      updateMeal({
        is_fasting: 1,
        items: [],
        name: meal.name || 'Digiuno',
        variant_group: null,
        variant_label: null,
      });
    } else {
      updateMeal({ is_fasting: 0 });
    }
  };

  const acceptAISuggest = (s: { name: string; items: Array<{ ingredient: string; quantity: string | null; unit: string | null }> }) => {
    updateMeal({
      name: s.name,
      items: s.items.map((it) => ({ ...it, calories: null })),
    });
  };

  // Drag&drop ingredienti
  const onDragStart = (idx: number) => (e: React.DragEvent) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(idx));
    } catch {
      // ignore
    }
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (targetIdx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === targetIdx) {
      setDragIndex(null);
      return;
    }
    const next = [...meal.items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIdx, 0, moved);
    updateMeal({ items: next });
    setDragIndex(null);
  };

  const totalKcal = meal.items.reduce((sum, it) => sum + (it.calories ?? 0), 0);

  return (
    <div
      style={{
        background: isFasting ? 'var(--ink)' : 'var(--surface)',
        color: isFasting ? '#fff' : 'var(--ink)',
        border: `1px solid ${isFasting ? 'var(--ink)' : 'var(--line)'}`,
        borderRadius: 10,
        padding: 14,
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        {meal.variant_label && (
          <span
            style={{
              background: 'var(--orange)',
              color: 'var(--ink)',
              fontSize: 10,
              fontWeight: 800,
              padding: '2px 7px',
              borderRadius: 4,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
            }}
          >
            Opz. {meal.variant_label}
          </span>
        )}
        <input
          type="text"
          value={meal.name}
          onChange={(e) => updateMeal({ name: e.target.value })}
          placeholder={isFasting ? 'Digiuno…' : 'Nome pasto'}
          aria-label="Nome pasto"
          title={meal.name || undefined}
          style={{
            flex: 1,
            minWidth: 0, // consente al campo nome di restringersi invece di spingere i bottoni fuori card
            border: `1px solid ${isFasting ? 'rgba(255,255,255,0.2)' : 'var(--line)'}`,
            borderRadius: 6,
            padding: '7px 10px',
            fontSize: 13,
            background: isFasting ? 'rgba(255,255,255,0.06)' : 'var(--surface)',
            color: isFasting ? '#fff' : 'var(--ink)',
            fontWeight: 600,
          }}
        />
        <button
          type="button"
          onClick={toggleFasting}
          aria-label="Toggle digiuno"
          title={isFasting ? 'Esci dal digiuno' : 'Imposta come digiuno'}
          style={{
            border: '1px solid var(--line)',
            background: isFasting ? 'var(--orange)' : 'var(--surface-2)',
            color: 'var(--ink)',
            padding: '6px 8px',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {isFasting ? <Sun size={13} /> : <Moon size={13} />}
        </button>
        <button
          type="button"
          onClick={onCopy}
          aria-label="Copia pasto"
          title="Copia pasto"
          style={{
            border: `1px solid ${isFasting ? 'rgba(255,255,255,0.2)' : 'var(--line)'}`,
            background: isFasting ? 'rgba(255,255,255,0.06)' : 'var(--surface)',
            color: isFasting ? '#fff' : 'var(--ink)',
            padding: '6px 8px',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Copy size={13} />
        </button>
        {hasClipboard && (
          <button
            type="button"
            onClick={onPaste}
            aria-label="Incolla pasto"
            title="Incolla pasto"
            style={{
              border: `1px solid ${isFasting ? 'rgba(255,255,255,0.2)' : 'var(--line)'}`,
              background: isFasting ? 'rgba(255,255,255,0.06)' : 'var(--surface-2)',
              color: isFasting ? '#fff' : 'var(--ink)',
              padding: '6px 8px',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Clipboard size={13} />
          </button>
        )}
        <button
          type="button"
          onClick={onRemove}
          aria-label="Rimuovi pasto"
          title="Rimuovi pasto"
          style={{
            border: `1px solid ${isFasting ? 'rgba(255,255,255,0.2)' : 'var(--line)'}`,
            background: isFasting ? 'rgba(255,255,255,0.06)' : 'var(--surface)',
            color: 'var(--red)',
            padding: '6px 8px',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Trash2 size={13} />
        </button>
      </div>

      {!isFasting && (
        <>
          {meal.items.length === 0 ? (
            <div
              style={{
                padding: '14px 10px',
                background: 'var(--surface-2)',
                borderRadius: 8,
                fontSize: 12.5,
                color: 'var(--mute)',
                textAlign: 'center',
                marginBottom: 8,
              }}
            >
              Nessun ingrediente.
            </div>
          ) : (
            meal.items.map((it, i) => (
              <div
                key={i}
                draggable
                onDragStart={onDragStart(i)}
                onDragOver={onDragOver}
                onDrop={onDrop(i)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '18px 1fr',
                  gap: 4,
                  alignItems: 'start',
                  opacity: dragIndex === i ? 0.5 : 1,
                }}
              >
                <div
                  aria-label="Trascina per riordinare"
                  title="Trascina per riordinare"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'grab',
                    color: 'var(--mute)',
                    paddingTop: 8,
                  }}
                >
                  <GripVertical size={13} />
                </div>
                <IngredientAutocomplete
                  item={it}
                  index={i}
                  onChange={(patch) => updateItem(i, patch)}
                  onRemove={() => removeItem(i)}
                />
              </div>
            ))
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, rowGap: 8, alignItems: 'center', marginTop: 6 }}>
            <button
              type="button"
              onClick={addItem}
              style={{
                border: '1px dashed var(--line)',
                background: 'transparent',
                color: 'var(--mute)',
                padding: '6px 12px',
                borderRadius: 6,
                fontSize: 12,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Plus size={12} /> Ingrediente
            </button>
            <AISuggestButton
              patientId={patientId}
              mealType={meal.meal_type as MealType}
              context={aiContext}
              onAccept={acceptAISuggest}
              label="AI"
            />
            {canAddVariant && nextVariantLabel && (
              <button
                type="button"
                onClick={onAddVariant}
                style={{
                  border: '1px solid var(--orange)',
                  background: 'var(--orange-soft, #fff3d6)',
                  color: 'var(--orange-deep, #a87c00)',
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  letterSpacing: 0.3,
                }}
              >
                + Variante {nextVariantLabel}
              </button>
            )}
            {totalKcal > 0 && (
              <span
                style={{
                  marginLeft: 'auto',
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  color: 'var(--mute)',
                  fontWeight: 600,
                }}
              >
                ~{totalKcal} kcal
              </span>
            )}
          </div>
        </>
      )}

      {isFasting && (
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', padding: '4px 0' }}>
          Slot saltato (digiuno). Niente ingredienti, niente calorie.
        </div>
      )}
    </div>
  );
}
